import React from "react";
import PropTypes from "prop-types";
import ActionLogger from "action-logger";
import { connect } from "react-redux";

import OneTimeModalsActions from "actions/one-time-modals-actions";

import { HappinessSurveyTypes, ONE_TIME_MODALS } from "rtr-constants";
import MembershipHelpers from "helpers/membership-helpers";
import { REFERRAL_REMINDER_MODAL } from "components/source/membership/modals/SmartReferralsReminderModal";

import sharedActions from "actions/shared-actions";
import happinessSurveyActions from "actions/happiness-survey-actions";
import smartComponentActions from "actions/smart-component-actions";

import HappinessSurveySelfServiceExchangeModal from "./hs_self_service_exchange/hs-self-service-exchange";
import HappinessSurveySubscription from "./hs_subscription/happiness-survey-subscription";

export class HappinessSurveyContainer extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    happinessSurvey: PropTypes.object,
    isHappinessSurveyFetched: PropTypes.bool,
    loadReferralsReminderModal: PropTypes.func,
    membershipState: PropTypes.object,
    smartComponentState: PropTypes.shape({
      didFetchSmartReferralsReminderModal: PropTypes.bool,
      referralsReminderModal: PropTypes.shape({
        name: PropTypes.string,
        CTA: PropTypes.string,
        body: PropTypes.string,
        title: PropTypes.string,
      }),
    }),
    oneTimeModals: PropTypes.object,
    isCustomerPromiseEligible: PropTypes.bool,
  };

  static completeStatus = "COMPLETE";
  static modalName = "HAPPINESS-SURVEY-MODAL";

  state = {
    surveyShownPixelSent: false,
    isModalOpen: true,
    isCustomerPromiseModalOpen: false,
  };

  async componentDidMount() {
    await this.updateCustomerPromiseModal();
  }

  async componentDidUpdate(prevProps) {
    // check if we initialized the HS and should fire the pixel
    if (
      this.props.happinessSurvey &&
      !this.state.surveyShownPixelSent &&
      this.props.happinessSurvey?.itemSurveys?.length
    ) {
      ActionLogger.logAction({
        object_type: "happiness_survey",
        action: "shown",
      });
      this.setState({
        surveyShownPixelSent: true,
      });

      if (!prevProps.displayedModal) {
        this.props.dispatch(sharedActions.displayModal(this.constructor.modalName));
      }
    }

    if (!this.props.isHappinessSurveyFetched && MembershipHelpers.isActiveMembership(this.props.membershipState)) {
      this.props.dispatch(happinessSurveyActions.happinessSurveyFetched(true));
      this.props.dispatch(happinessSurveyActions.fetchHappinessSurveys());
    }

    const shouldFetchSurveys =
      this.props.happinessSurvey?.itemSurveys?.length > prevProps.happinessSurvey?.itemSurveys?.length;

    if (shouldFetchSurveys) {
      this.props.dispatch(happinessSurveyActions.fetchHappinessSurveys());
    }

    if (
      !prevProps.smartComponentState?.didFetchSmartReferralsReminderModal &&
      this.props.smartComponentState?.didFetchSmartReferralsReminderModal &&
      this.props.smartComponentState.referralsReminderModal?.name &&
      this.props.smartComponentState.referralsReminderModal.name !== "noModal"
    ) {
      this.props.dispatch(sharedActions.displayModal(REFERRAL_REMINDER_MODAL));
    }

    if (
      prevProps.membershipState !== this.props.membershipState ||
      prevProps.oneTimeModals !== this.props.oneTimeModals
    ) {
      await this.updateCustomerPromiseModal();
    }
  }

  updateCustomerPromiseModal = async () => {
    const { isCustomerPromiseEligible } = this.props;
    const OTMCheck = !OneTimeModalsActions.isDismissedHelper(
      this.props.oneTimeModals,
      ONE_TIME_MODALS.CUSTOMER_PROMISE_MODAL
    );

    this.setState({ isCustomerPromiseModalOpen: isCustomerPromiseEligible && OTMCheck });
  };

  handleRequestClose = () => {
    // Product wants this non-closable modal to be closeable when clicking the overlay,
    // if all surveys are completed.
    const itemSurveysList = this.props?.happinessSurvey?.itemSurveys ?? [];
    const hasCompletedAllSurveys = itemSurveysList.every(s => s.status === this.constructor.completeStatus);

    if (hasCompletedAllSurveys && this.state.isModalOpen) {
      this.launchPostHappinessModals();
    }
  };

  launchPostHappinessModals() {
    const { loadReferralsReminderModal } = this.props;

    loadReferralsReminderModal();
  }

  closeHappinessSurvey = () => {
    const { happinessSurvey, dispatch } = this.props;
    const itemSurveysList = happinessSurvey?.itemSurveys ?? [];
    const hasCompletedAllSurveys = itemSurveysList.every(survey => survey.status === this.constructor.completeStatus);

    this.setState({ isModalOpen: false });
    dispatch(sharedActions.displayModal(false));

    if (hasCompletedAllSurveys) {
      this.launchPostHappinessModals();
      return;
    }
  };

  render() {
    const happinessSurvey = this.props.happinessSurvey || {};
    const itemSurveys = happinessSurvey?.itemSurveys?.length;

    if (!itemSurveys || this.state.isCustomerPromiseModalOpen) {
      return null;
    }

    if (happinessSurvey.type !== HappinessSurveyTypes.RESERVE) {
      return (
        <HappinessSurveySubscription
          closeHappinessSurvey={this.closeHappinessSurvey}
          handleRequestClose={this.handleRequestClose}
          isModalOpen={this.state.isModalOpen}
        />
      );
    } else {
      return (
        <HappinessSurveySelfServiceExchangeModal
          handleRequestClose={this.handleRequestClose}
          isModalOpen={this.state.isModalOpen}
        />
      );
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    dispatch: ownProps.dispatch,
    displayedModal: state.displayedModal,
    happinessSurvey: state.happinessSurvey,
    isHappinessSurveyFetched: state.isHappinessSurveyFetched,
    membershipState: MembershipHelpers.getMembershipState(state),
    smartComponentState: state.smartComponentState,
    oneTimeModals: state.oneTimeModals,
    userData: state.userData,
    isCustomerPromiseEligible: state.customerPromise?.isEligible,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    loadReferralsReminderModal: () => smartComponentActions.getReferralsReminderModal()(dispatch),
    dispatch,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HappinessSurveyContainer);
